import { useInView } from '../hooks/useInView';
import liveDispatchMap from '../assets/live dispatch map.png';
import meterApp from '../assets/Meter App.webp';
import driverOnboarding from '../assets/Driver Onboarding.png';
import './PlatformSection.css';

const WORKFLOW_STEPS = [
  {
    step: '01',
    label: 'Onboard',
    title: 'Driver Onboarding',
    desc: 'Documentation collection, compliance review, and digital signature workflows — through the EnrollMe driver self-service portal. Drivers complete onboarding on their phone before touching a vehicle.',
    color: 'yellow',
  },
  {
    step: '02',
    label: 'Dispatch',
    title: 'Dispatch Operations',
    desc: 'Booking management, automatic or manual driver assignment, and full dispatch workflow from the web-based admin dashboard — with click-to-assign from the live fleet map.',
    color: 'blue',
  },
  {
    step: '03',
    label: 'Drive',
    title: 'Mobile Trip Execution',
    desc: 'Drivers execute dispatched trips and flagdown pickups through the TaxiOps mobile app — with offline reliability ensuring no trip is ever lost due to poor connectivity.',
    color: 'green',
  },
  {
    step: '04',
    label: 'Track',
    title: 'Live Fleet Visibility',
    desc: 'Live dispatch mapping with positions updated every 12 seconds, fleet status, driver presence, HOS monitoring, and real-time GPS tracking for every active trip.',
    color: 'blue',
  },
  {
    step: '05',
    label: 'Complete',
    title: 'Receipts & Reporting',
    desc: 'Trip completion, fare records, and receipts — with full reporting across trips, driver earnings, HOS logs, compliance status, and financial summaries, all exportable to CSV.',
    color: 'green',
  },
];

const FEATURES = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM3 18a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Driver Onboarding',
    desc: 'Structured onboarding — documentation collection, review workflows, and approval from application to first shift, through EnrollMe Compliance Tower.',
    accent: 'yellow',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="4" y="2" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M12 15l2 2 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'E-Signature Workflows',
    desc: 'Compliance teams send signature requests through the portal. Drivers sign by drawing with their finger or typing — full audit trail stored permanently.',
    accent: 'yellow',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="5" y="1" width="10" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8 5h4M8 8h4M8 11h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <circle cx="10" cy="15" r="1.2" fill="currentColor" opacity=".5"/>
      </svg>
    ),
    title: 'Driver Self-Service Portal',
    desc: 'Mobile-first portal where drivers complete onboarding, upload documents, sign agreements, and manage renewals throughout their time with the fleet — no app download required.',
    accent: 'yellow',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M6 17.5A8 8 0 0 1 6 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity=".35"/>
      </svg>
    ),
    title: 'Expiration Centre',
    desc: 'Fleet-wide compliance expiration tracking across all driver and vehicle documents — with urgency buckets and automated renewal notifications to drivers.',
    accent: 'green',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 13l4-4 3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="2" y="3" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M14 9h4M16 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Compliance Exports',
    desc: 'Configurable compliance exports for account work, internal review, and regulated transportation requirements - formatted for consistent review and recordkeeping.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="5" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M14 5V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M2 10h16" stroke="currentColor" strokeWidth="1.4"/>
        <circle cx="10" cy="14" r="1.2" fill="currentColor" opacity=".5"/>
      </svg>
    ),
    title: 'Booking & Trip Management',
    desc: 'End-to-end trip lifecycle from booking through completion — with a full status trail, live location map, and route history for every trip.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M10 2v2M10 16v2M2 10h2M16 10h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Dispatch Operations',
    desc: 'Full dispatch workflow with automatic or manual assignment, 45-second offer timers, and click-to-assign from the live fleet map.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 15l3-3 4 2 4-6 3 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="2" y="3" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.4"/>
      </svg>
    ),
    title: 'Driver & Vehicle Management',
    desc: 'Fleet personnel and vehicle records with compliance badge tracking — licence, DOT, CBI, MVR, and fingerprint clearance status visible at a glance.',
    accent: 'green',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 12v6M7 15l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Live Dispatch Mapping',
    desc: 'Every driver on shift shown live on the map with cab number and availability — positions updated every 12 seconds as drivers move.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="5" y="2" width="10" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8 6h4M8 9h4M8 12h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="13" cy="15" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M12.3 15l.5.5.9-.9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Mobile Trip Execution',
    desc: 'Driver app for dispatched trips and flagdown pickups — with digital metering, flat rate selection, additional fee options, and automatic trip summary.',
    accent: 'green',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 10h12M4 6h12M4 14h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="16" cy="14" r="3" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M15 14h2M16 13v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Flagdown & Metered Fares',
    desc: 'Street pickups initiated from the driver app — meter calculates base fare, distance rate, and wait time in real time, updated every ten seconds.',
    accent: 'yellow',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Shift & HOS Management',
    desc: 'Driver shift lifecycle with active HOS monitoring. Configurable daily limits with escalating warnings — new trips blocked automatically at the service ceiling.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 12a7 7 0 1 1 14 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M3 12h14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M7 16l-4 0M13 16l4 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity=".4"/>
        <path d="M10 12v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Offline Trip Reliability',
    desc: 'Drivers never lose a trip due to poor connectivity. Trips start, run, and complete offline — all data queued locally and synced automatically on reconnection.',
    accent: 'green',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2a6 6 0 0 1 6 6c0 4-6 10-6 10S4 12 4 8a6 6 0 0 1 6-6z" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="10" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M15 3l2-1M17 6h2M15 9l2 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".5"/>
      </svg>
    ),
    title: 'Push Notifications',
    desc: 'New job assignments reach drivers even when the app is in the background or the phone screen is off — via Google and Apple push notification services.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2l1.8 4.2L16 7.6l-3 2.9.7 4.1L10 12.4l-3.7 2.2.7-4.1-3-2.9 4.2-.6L10 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
        <path d="M4 17h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity=".4"/>
      </svg>
    ),
    title: 'Weekly Driver Motivation',
    desc: 'Automatic weekly recognition every Monday — personalised tiered messages based on performance rank and week-on-week improvement, delivered without pressure or mid-shift interruption.',
    accent: 'yellow',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M5 3h10a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 7h6M7 10h6M7 13h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M13 12h3v2h-3z" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
    ),
    title: 'Fare Records',
    desc: 'Complete fare documentation for every trip — metered, flat rate, or flagdown — with all additional charges, distances, and wait times recorded.',
    accent: 'green',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M5 3h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 8h6M7 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M13 14l2 2 2-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Trip Completion Recap',
    desc: 'Structured completion workflow — driver confirms final fare, total distance, wait time, and trip duration before the dashboard unlocks for the next job.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 4h12v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M2 4h16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M7 9h6M7 12h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Reports & Analytics',
    desc: 'Custom report builder for trips, HOS logs, driver compliance, vehicle compliance, earnings, and financial reporting — with date presets and CSV export.',
    accent: 'green',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 16l4-8 3 5 2-3 3 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 4h16" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Driver Messaging',
    desc: 'Broadcast and targeted messaging between dispatch, compliance teams, and drivers — with delivery tracking, scheduled sending, and read status.',
    accent: 'blue',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 3v4M10 13v4M3 10h4M13 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    title: 'Accident Reporting',
    desc: 'Structured incident documentation with severity levels, photo uploads, cost allocation per driver or vehicle, and status tracking from report to closure.',
    accent: 'yellow',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M7 9h4M9 7v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Diagnostics',
    desc: 'Per-driver diagnostic view — active trip state, sync queue depth, pending upload counts, and a Retry Sync Now tool for field troubleshooting.',
    accent: 'green',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2l1.8 4.2L16 7.6l-3 2.9.7 4.1L10 12.4l-3.7 2.2.7-4.1-3-2.9 4.2-.6L10 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
        <circle cx="10" cy="9.5" r="1.5" fill="currentColor" opacity=".4"/>
      </svg>
    ),
    title: 'Operations Intelligence',
    desc: 'Demand, dispatch, fleet, HOS, diagnostics, and trend signals are organized into a focused command view with risks, evidence, and recommended actions.',
    accent: 'ai',
  },
];

const ACCENT_MAP = {
  yellow: { bg: 'var(--yellow-glow)', border: 'var(--border-yellow)', color: 'var(--yellow)' },
  blue:   { bg: 'var(--blue-glow)',   border: 'var(--border-blue)',   color: 'var(--blue-light)' },
  green:  { bg: 'var(--green-glow)',  border: 'rgba(16,185,129,0.25)', color: 'var(--green-light)' },
  ai:     { bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.25)', color: '#a78bfa' },
};

function FeatureCard({ icon, title, desc, accent, className }) {
  const a = ACCENT_MAP[accent] || ACCENT_MAP.blue;
  return (
    <div className={`feat-card ${className || ''}`}>
      <div
        className="feat-card__icon"
        style={{ background: a.bg, border: `1px solid ${a.border}`, color: a.color }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <h3 className="feat-card__title">{title}</h3>
      <p className="feat-card__desc">{desc}</p>
    </div>
  );
}

export default function PlatformSection() {
  const [headerRef, headerInView] = useInView();
  const [workflowRef, workflowInView] = useInView();
  const [gridRef, gridInView] = useInView();
  const [screenshotRef, screenshotInView] = useInView();

  return (
    <section className="platform" id="platform" aria-labelledby="platform-heading">
      <div className="container">
        {/* Header */}
        <div className="platform__header" ref={headerRef}>
          <p className={`section-label fade-up ${headerInView ? 'in-view' : ''}`}>
            Platform
          </p>
          <h2
            id="platform-heading"
            className={`platform__heading fade-up d1 ${headerInView ? 'in-view' : ''}`}
          >
            The Complete Transportation<br />
            <span className="gradient-text">Operations Platform</span>
          </h2>
          <p className={`platform__sub fade-up d2 ${headerInView ? 'in-view' : ''}`}>
            TaxiOps and EnrollMe Compliance Tower cover the full operational lifecycle
            in one connected cloud platform — from first driver document and compliance
            signature through every trip, fare, and shift record.
          </p>
        </div>

        {/* Two-product architecture */}
        <div className={`platform__products fade-up d3 ${headerInView ? 'in-view' : ''}`}>
          <div className="platform__product">
            <div className="platform__product-badge">Product One</div>
            <h3 className="platform__product-title">TaxiOps Platform</h3>
            <p className="platform__product-desc">
              Live operations — driver dispatch, digital metering, GPS fleet tracking,
              hours-of-service management, trip history, reporting, and a built-in weekly
              driver motivation engine. Available on Android and iPhone, with a web-based
              admin dispatch dashboard.
            </p>
            <div className="platform__product-items">
              <span className="platform__product-item">Driver App — Android &amp; iOS</span>
              <span className="platform__product-item">Admin Dispatch Dashboard</span>
              <span className="platform__product-item">Live Fleet Map</span>
              <span className="platform__product-item">Weekly Motivation Engine</span>
            </div>
          </div>
          <div className="platform__product-plus" aria-hidden="true">+</div>
          <div className="platform__product platform__product--enrollme">
            <div className="platform__product-badge platform__product-badge--green">Product Two</div>
            <h3 className="platform__product-title">EnrollMe Compliance Tower</h3>
            <p className="platform__product-desc">
              Driver and vehicle compliance — onboarding, document collection and review,
              e-signature workflows, expiration tracking, automated renewal notifications,
              and compliance exports. With a dedicated admin portal and a
              mobile-first driver self-service portal.
            </p>
            <div className="platform__product-items">
              <span className="platform__product-item">Admin Compliance Portal</span>
              <span className="platform__product-item">Driver Self-Service Portal</span>
              <span className="platform__product-item">Expiration Centre</span>
              <span className="platform__product-item">Compliance Exports</span>
            </div>
          </div>
        </div>
        <p className={`platform__products-note fade-up d4 ${headerInView ? 'in-view' : ''}`}>
          Both products run in the cloud, update in real time, and share the same understanding
          of every driver — so the compliance status a compliance officer sees is the same
          status the dispatcher sees before assigning a job.
        </p>

        {/* Workflow steps */}
        <div className="platform__workflow" ref={workflowRef} aria-label="Operational workflow">
          <div className={`platform__workflow-headline fade-up ${workflowInView ? 'in-view' : ''}`}>
            <span className="platform__workflow-wordmark">
              Onboard<span>.</span>&nbsp;Dispatch<span>.</span>&nbsp;Drive<span>.</span>&nbsp;Track<span>.</span>&nbsp;Complete<span>.</span>
            </span>
          </div>
          <div className={`platform__steps fade-in d1 ${workflowInView ? 'in-view' : ''}`}>
            {WORKFLOW_STEPS.map((s, i) => (
              <div key={s.step} className={`platform__step platform__step--${s.color}`}>
                <div className="platform__step-number" aria-hidden="true">{s.step}</div>
                <div className="platform__step-label">{s.label}</div>
                <div className="platform__step-title">{s.title}</div>
                <p className="platform__step-desc">{s.desc}</p>
                {i < WORKFLOW_STEPS.length - 1 && (
                  <div className="platform__step-arrow" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 8h8M9 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Feature grid */}
        <div className="platform__grid-section" ref={gridRef}>
          <div className={`platform__grid-header fade-up ${gridInView ? 'in-view' : ''}`}>
            <h3 className="platform__grid-title">Platform Capabilities</h3>
            <p className="platform__grid-sub">
              Two integrated products — 25+ capabilities — covering every stage of running a professional fleet.
            </p>
          </div>
          <div className="platform__grid">
            {FEATURES.map((f, i) => (
              <FeatureCard
                key={f.title}
                {...f}
                className={`fade-up d${Math.min(i % 4 + 1, 8)} ${gridInView ? 'in-view' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Screenshots row */}
        <div
          className={`platform__screenshots fade-up d1 ${screenshotInView ? 'in-view' : ''}`}
          ref={screenshotRef}
        >
          <div className="platform__screenshot-item">
            <img src={liveDispatchMap} alt="TaxiOps Live Dispatch Map" className="platform__screenshot-img" />
            <p className="platform__screenshot-label">Live Dispatch Map</p>
          </div>
          <div className="platform__screenshot-item">
            <img src={meterApp} alt="TaxiOps Driver App — Metered Fare" className="platform__screenshot-img" />
            <p className="platform__screenshot-label">Driver App — Metered Fare</p>
          </div>
          <div className="platform__screenshot-item">
            <img src={driverOnboarding} alt="TaxiOps Driver Onboarding" className="platform__screenshot-img" />
            <p className="platform__screenshot-label">Driver Onboarding</p>
          </div>
        </div>
      </div>
    </section>
  );
}
